import { css } from '@emotion/core';

import * as utils from './utils';

export const sizes = {
  giant: 1200,
  desktop: 992,
  tablet: 768,
  phone: 576,
  smallPhone: 414,
  iphone6: 375,
};

// iterate through the sizes and create a media template
export const media = Object.keys(sizes).reduce((acc, label) => {
  // use em in breakpoints to work properly cross-browser and support users
  // changing their browsers font-size: https://zellwk.com/blog/media-query-units/
  const emSize = sizes[label] / 16;
  return {
    ...acc,
    [label]: (...args) => css`
      @media (min-width: ${emSize}em) {
        ${css(...args)}
      }
    `,
  };
}, {});

export const breakpoints = {
  xs: 0,
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
};

export const styledSystemBreakpoints = [...Object.values(breakpoints).slice(1)];

const pinks = [
  '#FEF5F8',
  '#FFEBE0',
  '#FCCFDE',
  '#FF94B1',
  '#BB3763',
  '#471E08',
  '#763249',
  '#643545',
  '#3D252D',
  '#cc3788',
];

export const colors = {
  bgGrey: '#e6e6e6',
  brand: '#9B59B6',
  linkWater: '#DFEFF6',
  aliceBlue: '#EFF6FF',
  oldLace: '#FDF3E6',
  successBg: '#DCF5E3',
  success: '#004D01',
  codGrey: '#0E0E0E',
  mineShaft: '#2E2E2E',
  pinks,
  brandSubtle: '#bfb3ff',
  alertText: '#856404',
  accent: '#ffd326',
  accent800: '#FFD73B',
  accent700: '#FFDB50',
  accent600: '#FFE066',
  accent500: '#FFE47B',
  accent400: '#FFE890',
  accent300: '#FFECA5',
  accent200: '#FFF1BB',
  accent100: '#FFF5D0',
  accentSubtle: '#fff9e5',
  defaultText: '#333',
  mutedText: '#666',
  lightText: '#999',
  border: '#e6e6e6',
  background: '#f8f8f8',
  white: '#fff',
  black: '#000',
  photoGradientStart: 'rgba(0, 0, 0, 0)',
  photoGradientEnd: 'rgba(0, 0, 0, 0.4)',
  red: '#e74c3c',
  errorSubtle: '#fdbfc6',
  memoirBlue: '#015F9B',
  grey80: '#CCCACC',
  grey90: '#f2f2f2',
  grey60: '#999',
  grey20: '#333333',
  blueBackground: '#0067CC',
  darkBlue: '#36247a',
  green: '#2ECC71',
  noticeIntense: '#FFD326',
  childJournal: '#1AAFD0',
  memoir: '#5B5F7B',
  // refactoring UI attempt
  'cyan-050': '#E0FCFF',
  'cyan-100': '#BEF8FD',
  'cyan-200': '#87EAF2',
  'cyan-300': '#54D1DB',
  'cyan-400': '#38BEC9',
  'cyan-500': '#2CB1BC',
  'cyan-600': '#14919B',
  'cyan-700': '#0E7C86',
  'cyan-800': '#0A6C74',
  'cyan-900': '#044E54',
  // drawer
  drawerBg: '#FFF',
  drawerSubtle: 'hsl(251, 8%, 75%)',
  drawerBasicallyWhite: 'hsl(251, 85%, 96%)',
  // colors from the new style guide:
  // https://www.lifetales.com/style-guide
  jPrimary1: '#7a68bf',
  jPrimary2: '#ffa96a',
  jPrimary3: '#001E42',
  special: 'linear-gradient(90deg, #3866F4 0%, #7B33FB 48.96%, #BD00FF 100%)',
};

export const fonts = {
  sanFrancisco: '"SF UI Text", "Roboto", sans-serif',
  quicksand: 'Poppins',
  sans:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif;',
};
fonts.heading = fonts.quicksand;
fonts.body = fonts.sanFrancisco;

export const shadows = {
  small: '0 2px 4px rgba(0, 0, 0, 0.15)',
  container: '0 2px 10px 0 rgba(0,0,0,0.15)',
  navbar: '0 2px 0 0 #e6e6e6',
  // taken from refactoring UI elevation with ambient light
  lowest: '0 1px 3px rgba(0,0,0,.12), 0 1px 2px rgba(0,0,0,.24)',
  low: '0 3px 6px rgba(0,0,0,.15), 0 2px 4px rgba(0,0,0,.12)',
  medium: '0 10px 20px rgba(0,0,0,.15), 0 3px 6px rgba(0,0,0,.10)',
  high: '0 15px 25px rgba(0,0,0,.15), 0 5px 10px rgba(0,0,0,.05)',
  highest: '0 20px 40px rgba(0,0,0,.2)',
};

export const borderRadii = {
  large: '8px',
  normal: '6px',
  small: '3px',
  container: '15px',
};

export const gutter = '20px';

export const blur = 'blur(4px)';

const transition = 'all 0.175s ease';

const outlineStyles = {
  backgroundColor: colors.white,
  color: colors.brand,
  border: `1px solid ${colors.brand}`,
  transition,
  fontWeight: 'normal',
};

const disabledStyle = {
  height: '44px',
  py: 'unset !important',
  '&:disabled': {
    opacity: 0.5,
    cursor: 'no-drop',
  },
};

const hoverStyle = {
  '&:hover': {
    boxShadow: 'inset 0 0 100px 100px rgba(0, 0, 0, 0.1)',
  },
};

const buttons = {
  link: {
    background: 'none',
    padding: 0,
    fontWeight: 'normal',
    color: colors.brand,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  special: {
    variant: 'buttons.primary',
    background: colors.special,
    boxShadow: '0px 10px 22px 4px rgba(99, 105, 239, 0.2)',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '20px',
    lineHeight: '120%',
  },
  primary: {
    ...disabledStyle,
    ...hoverStyle,
    background: colors.brand,
    borderRadius: '23px',
    color: colors.white,
    border: `1px solid ${colors.brand}`,
    transition,
  },
  outline: {
    ...disabledStyle,
    ...outlineStyles,
  },
  muted: {
    ...disabledStyle,
    ...hoverStyle,
    transition,
    // backgroundColor: colors.grey90,
    border: '1px solid #B1B1B1',
    color: colors.mutedText,
    borderRadius: '23px',
  },
  mutedOutline: {
    ...disabledStyle,
    ...outlineStyles,
    color: colors.mutedText,
    border: `1px solid ${colors.border}`,
    '&:hover': {
      border: `1px solid ${colors.brand}`,
      color: colors.brand,
    },
  },
  destructiveOutline: {
    ...disabledStyle,
    ...outlineStyles,
    color: colors.red,
    border: `1px solid ${colors.red}`,
    '&:hover': {
      border: `1px solid ${colors.red}`,
      color: colors.red,
    },
  },
  destructive: {
    ...disabledStyle,
    color: colors.white,
    backgroundColor: colors.red,
    border: `1px solid ${colors.red}`,
    fontWeight: 'normal',
    transition,
  },
  dark: {
    ...disabledStyle,
    color: colors.white,
    backgroundColor: 'rgba(0,0,0,.6)',
  },
  darkIconCircle: {
    ...disabledStyle,
    display: 'inline-flex',
    color: colors.white,
    backgroundColor: 'rgba(0,0,0,.6)',
    padding: '.5em',
    borderRadius: '50%',
    transition,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,.8)',
    },
  },
  iconCircle: {
    ...disabledStyle,
    display: 'inline-flex',
    color: colors.brand,
    backgroundColor: colors.white,
    padding: '.5em',
    borderRadius: '50%',
    transition,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,.8)',
    },
  },
  transparent: {
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    color: 'inherit',
    fontWeight: 'normal',
  },
  unset: {
    all: 'unset',
    padding: 0,
    border: 'none',
    background: 'none',
    fontWeight: 'inherit',
    cursor: 'pointer',
  },
  videoEdit: {
    bg: '#1A1A1A',
    color: 'white',
    border: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    '&:disabled': { color: '#595959' },
  },
};

const cards = {
  error: {
    background: colors.errorSubtle,
    color: colors.red,
  },
};

export default {
  breakpoints: styledSystemBreakpoints,
  keyedBreakpoints: breakpoints,
  buttons,
  text: {
    paragraph: {
      color: '#0E0E0E',
      fontFamily: 'Poppins',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '120%',
      letterSpacing: '0.16px',
    },
  },
  colors,
  colors: colors,
  fonts,
  shadows,
  borderRadii,
  media,
  gutter,
  blur,
  utils,
  cards,
  space: [0, 8, 16, 24, 32, 48, 64, 72, 128, 256],
};
